import Chart from './Chart';
import Tooltip from './Tooltip';
import * as d3 from 'd3';

export default class GroupedBar extends Chart {
  constructor(svg, height, width, margin, trnsTime, tooltipDiv) {
    super(svg, height, width, margin, trnsTime);

    this.tooltip = new Tooltip(tooltipDiv);
    this._initializeHelperFunction();
    this._initializeChart();
  }

  _initializeHelperFunction() {
    this.dateParse = d3.timeParse('%Y');

    this.xPosition = d3.scaleBand()
      .domain([0, 1])
      .range([0, this.width - this.margin.left - this.margin.right])
      .padding(0.1);

    this.yPosition = d3.scaleLinear()
      .domain([0, 1])
      .range([0, this.height - this.margin.top - this.margin.bottom]);
  }

  _initializeChart() {
    // Make SVG Responsive
    this.svg
      .attr('viewBox', `0 0 ${this.width} ${this.height}`)
      .attr('preserveAspectRatio' , 'xMinYMin meet');

    // X-Axis Group
    this.xAxis = this.svg.append('g')
      .attr('transform', 
        `translate(${this.margin.left},${this.height - this.margin.bottom})`)
      .call(d3.axisBottom(this.xPosition));

    // X-Axis Label
    this.xLabel = this.svg.append('g')
      .append('text')
        .attr('transform', 
          `translate(${(this.width - this.margin.right + this.margin.left) / 2},
          ${this.height})`)
        .style('text-anchor', 'middle')
        .text('Year');
    
    // Y-Axis Group
    this.yAxis = this.svg.append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`)
      .call(d3.axisLeft(this.yPosition).tickSize(0));

    // Y-Axis Label
    this.yLabel = this.svg.append('g')
      .append('text')
        .attr('transform', `translate(${this.margin.left * 0.3},
          ${this.height / 2})rotate(${-90})`)
        .style('text-anchor', 'middle')
        .text(this.units);

    // Bars Group
    this.bars = this.svg.append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

    // Legend Group
    this.legend = this.svg.append('g')
      .attr('transform', `translate(${this.width - this.margin.right + 
        this.margin.left * 1.5},${this.margin.top+10})`);
  }

  drawChart() {
    this.color.domain(this.data.map(d => d.key).sort(d3.ascending));

    this._drawYaxis();
    this._drawXaxis();
    this._drawBars();
    this._drawLegend();
  }

  _drawXaxis() {
    const [min, max] = [d3.min(this.data, d => d.minYear),
      d3.max(this.data, d => d.maxYear)];
    const yearRange = d3.timeYears(min, max);
    yearRange.push(max);

    this.xPosition
      .domain(yearRange);

    this.xAxis
      .transition()
      .duration(this.transitionTime)
        .call(
          d3.axisBottom(this.xPosition)
            .ticks(d3.timeYear.every(1))
            .tickFormat(d3.timeFormat('%Y'))
            .tickSizeOuter(0.01)
          );

    this.xAxis
      .selectAll('path, line')
        .attr('color', 'gray');
  }

  _drawYaxis() {
    this.yPosition
      .domain([d3.max(this.data, d => d3.max(d.value, d => d.value)), 0.1])
      .nice();

    this.yAxis
      .call(d3.axisLeft(this.yPosition))
      .selectAll('line, path')
        .attr('color', 'gray');

    this.yLabel
      .text(this.units);
  }

  _drawBars() {
    this.bars.selectAll('*').remove();

    const keys = this.data.map(val => val.key);
    console.log(this.data);

    const groupPosition = d3.scaleBand()
      .domain(keys)
      .range([0, this.xPosition.bandwidth()])
      .padding(0);
    
    this.bars
      .selectAll('g')
      .data(this.data)
      .enter()
      .append('g')
      .selectAll('rect')
      .data(d => d.value)
      .enter()
      .append('rect')
        .attr('x', d => this.xPosition(this.dateParse(d.year)) - 
          groupPosition(d.program) + this.xPosition.bandwidth())
        .attr('y', d => this.yPosition(d.value))
        .attr('width', groupPosition.bandwidth())
        .attr('height', d => this.height - this.yPosition(d.value) - 
          this.margin.top - this.margin.bottom)
        .attr('fill', d => this.color(d.program))
        .style('opacity', 0.6);
  }

  _drawLegend() {
    this.legend.selectAll('*').remove();

    // Legend Positioning and Text
    this.legend
      .selectAll('text')
      .data(this.data)
      .enter()
      .append('text')
        .attr('transform', (d, i) => `translate(${0},${i * 20})`);

    // Legend Color References
    this.legend
      .selectAll('circle')
      .data(this.data)
      .enter()
      .append('circle')
        .attr('r', '0.4em')
        .attr('cx', -5)
        .attr('cy', -5)
        .attr('transform', (d, i) => `translate(${-12},${i * 20})`)
        .attr('fill', (d, i) => this.color(d.key))
        .style('opacity', 0.6);

    this.legend
      .selectAll('circle, text')
        .on('mouseover', (d) => {
          this.tooltip.show(1);
          this.tooltip.changeContent(d.key);
        })
        .on('mouseleave', () => {
          this.tooltip.hide();
        })
        .on('mousemove', () => {
          this.tooltip.move(d3.event.pageX + 10, d3.event.pageY - 25);
        });
  }

  setData(data) {
    this.data = data.map(d => {
      [d.minYear, d.maxYear] = d3.extent(d.value, d => this.dateParse(d.year));
      return d;
    });
  }
}